import React from 'react';
import { Helmet } from 'react-helmet';
import Footer from './../../components/Footer';

const PiotroskiScoreGuide = () => {
  const title = "Piotroski Skoru: Değer Yatırımcıları İçin Basit Rehber | Smart Yatırım";
  const description = "Piotroski Skoru'nun ne olduğunu ve değer yatırımında nasıl kullanılacağını öğrenin. Akıllı yatırım kararları için detaylı rehber.";
  const currentYear = new Date().getFullYear();

  const scoreCriteria = [
    {
      title: 'Kârlılık Kriterleri',
      description: 'Pozitif net gelir, pozitif işletme nakit akışı ve işletme nakit akışının net geliri aşması gibi kriterler.'
    },
    {
      title: 'Kaldıraç, Likidite ve Kaynak Kullanımı',
      description: 'Düşük borç/varlık oranı, artan likidite ve yeni hisse ihracı olmaması gibi faktörler.'
    },
    {
      title: 'Operasyonel Verimlilik',
      description: 'Artan brüt marj ve artan varlık devir hızı.'
    }
  ];

  const benefits = [
    'Şirketlerin finansal sağlıklarını değerlendirmede kolaylık sağlar.',
    'Değer yatırımcılarına düşük riskli fırsatlar sunar.',
    'Karmaşık finansal analizlerin basitleştirilmesine yardımcı olur.'
  ];

  const tips = [
    'Piotroski Skoru yüksek olan şirketlere öncelik verin.',
    'Skoru tek başına değil, diğer temel analiz yöntemleriyle birlikte değerlendirin.',
    'Yatırım yapmadan önce sektör ve piyasa koşullarını dikkate alın.',
    'Smart Portföy ile şirketlerin Piotroski Skorlarını takip edin ve riskleri minimize etmek için portföyünüzü optimize edin. '
  ];

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <meta name="robots" content="index, follow" />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content="/piotroski-score-guide" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={title} />
        <meta name="twitter:description" content={description} />
        <link rel="canonical" href="/piotroski-score-guide" />
        <meta name="keywords" content="piotroski skoru, değer yatırımı, finansal analiz, yatırım stratejisi, finansal sağlık" />
      </Helmet>

      <div className="min-h-screen bg-gray-900 text-gray-100">
        {/* Hero Section */}
        <header className="bg-gradient-to-r from-blue-900 to-purple-900 py-32">
          <div className="container mx-auto px-4 max-w-6xl">
            <h1 className="text-5xl font-bold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-purple-400 no-underline select-none">
              Piotroski Skoru Nedir ve Nasıl Kullanılır?
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl leading-relaxed">
              Piotroski Skoru ile şirketlerin finansal sağlıklarını değerlendirin ve değer yatırımlarınızda daha akıllı kararlar alın.
            </p>
          </div>
        </header>

        {/* Image Section with Gradient Overlay */}
        <div className="relative h-48"> {/* Reduced height */}
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/50 to-gray-900 z-10"></div>
          <img 
            src="/images/blog/piotroski-score/piotroski-score-guide.jpeg" 
            alt="Piotroski Skoru İllüstrasyonu"
            className="w-full h-full object-cover"
          />
        </div>

        {/* Content Section */}
        <div className="container mx-auto px-4 max-w-6xl py-16">
          <div className="grid md:grid-cols-2 gap-12">
            {/* Left Column */}
            <div className="space-y-8">
              <section>
                <h2 className="text-3xl font-bold mb-6 text-blue-400 no-underline">
                  Piotroski Skoru Kriterleri
                </h2>
                <div className="space-y-4">
                  {scoreCriteria.map((criterion, index) => (
                    <div key={index} className="bg-gray-800 p-6 rounded-lg">
                      <h3 className="text-xl font-bold mb-2 text-purple-400 no-underline">
                        {criterion.title}
                      </h3>
                      <p className="text-gray-300">{criterion.description}</p>
                    </div>
                  ))}
                </div>
              </section>

              <section>
                <h2 className="text-3xl font-bold mb-6 text-blue-400 no-underline">
                  Piotroski Skoru Neden Önemlidir?
                </h2>
                <ul className="space-y-4">
                  {benefits.map((benefit, index) => (
                    <li key={index} className="flex items-start space-x-4">
                      <span className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-gray-900 font-bold">
                        {index + 1}
                      </span>
                      <span className="text-gray-300">{benefit}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>

            {/* Right Column */}
            <div className="space-y-8">
              <section className="bg-gray-800 p-8 rounded-lg">
                <h2 className="text-3xl font-bold mb-6 text-blue-400 no-underline">
                  Piotroski Skoru Kullanım İpuçları
                </h2>
                <ul className="space-y-4">
                  {tips.map((tip, index) => (
                    <li key={index} className="flex items-start space-x-4">
                      <span className="flex-shrink-0 w-8 h-8 bg-purple-500 rounded-full flex items-center justify-center text-gray-900 font-bold">
                        {index + 1}
                      </span>
                      <span className="text-gray-300">{tip}</span>
                    </li>
                  ))}
                </ul>
              </section>
            </div>
          </div>
        </div>

        {/* Schema.org structured data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            "headline": "Piotroski Skoru: Değer Yatırımcıları İçin Basit Rehber",
            "description": description,
            "author": {
              "@type": "Organization",
              "name": "Smart Yatırım"
            },
            "publisher": {
              "@type": "Organization",
              "name": "Smart Yatırım"
            },
            "datePublished": `${currentYear}-01-25T00:00:00Z`,
            "dateModified": `${currentYear}-01-25T00:00:00Z`,
            "mainEntityOfPage": {
              "@type": "WebPage",
              "@id": "/piotroski-score-guide"
            }
          })}
        </script>
      </div>

      <Footer />
    </>
  );
};

export default PiotroskiScoreGuide;
